import React, { useState } from "react";
import { BookOpen, X, Plus } from "lucide-react";
import { toast } from "sonner";

export const GroupModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  isEditing = false
}) => {
  const [name, setName] = useState(initialData?.name || "");
  const [category, setCategory] = useState(initialData?.category || "Câmara de Instrução");
  const [degreeTarget, setDegreeTarget] = useState(initialData?.degree_target || "Todos");
  const [coordinatorName, setCoordinatorName] = useState(initialData?.coordinator_name || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [meetingSchedule, setMeetingSchedule] = useState(initialData?.meeting_schedule || "Quinzenal");
  const [topicsStr, setTopicsStr] = useState(
    initialData?.topics_or_tasks ? initialData.topics_or_tasks.join("\n") : ""
  );

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Informe o nome do grupo ou comissão.");
      return;
    }

    const topicsList = topicsStr
      .split("\n")
      .map(t => t.trim())
      .filter(Boolean);

    const payload = {
      name: name.trim(),
      category,
      degree_target: degreeTarget,
      coordinator_name: coordinatorName.trim() || "Venerável Mestrado",
      description: description.trim(),
      meeting_schedule: meetingSchedule.trim(),
      topics_or_tasks: topicsList,
      member_ids: []
    };

    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div 
        data-testid="modal-group-form"
        className="w-full max-w-lg bg-[#131C2E] border border-[#D4AF37]/40 rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.25)] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center space-x-2">
            <BookOpen className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-serif font-bold text-lg text-white">
              {isEditing ? "Editar Grupo de Instrução" : "Criar Câmara de Instrução / Comissão"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs text-slate-300">
          
          <div>
            <label className="block mb-1 font-semibold text-slate-300">Nome do Grupo / Câmara *</label>
            <input
              data-testid="input-group-name"
              type="text"
              required
              placeholder="Ex: Câmara de Instrução de Aprendizes"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Categoria</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                <option value="Câmara de Instrução">Câmara de Instrução</option>
                <option value="Comissão Permanente">Comissão Permanente</option>
                <option value="Comissão Especial">Comissão Especial</option>
                <option value="Ação Filantrópica">Ação Filantrópica</option>
              </select>
            </div>

            <div>
              <label className="block mb-1 font-semibold text-slate-300">Grau Alvo</label>
              <select
                value={degreeTarget}
                onChange={(e) => setDegreeTarget(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                <option value="Todos">Todos os Graus</option>
                <option value="Aprendiz">Aprendiz</option>
                <option value="Companheiro">Companheiro</option>
                <option value="Mestre">Mestre</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Coordenador / Responsável</label>
              <input
                type="text"
                placeholder="Ex: Ir. 2º Vigilante"
                value={coordinatorName}
                onChange={(e) => setCoordinatorName(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              />
            </div>

            <div>
              <label className="block mb-1 font-semibold text-slate-300">Cronograma / Periodicidade</label>
              <input
                type="text"
                placeholder="Ex: Quinzenal às Quintas"
                value={meetingSchedule}
                onChange={(e) => setMeetingSchedule(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold text-slate-300">Objetivo / Descrição do Programa</label>
            <textarea
              rows="2"
              placeholder="Descreva o propósito deste programa de estudos..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold text-slate-300">
              Temas / Peças de Arquitetura (um por linha)
            </label>
            <textarea
              rows="3"
              placeholder="Desbaste da Pedra Bruta&#10;As 3 Grandes Luzes&#10;Simbolismo do Silêncio"
              value={topicsStr}
              onChange={(e) => setTopicsStr(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs font-mono"
            />
          </div>

          <div className="pt-4 border-t border-slate-800 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white font-semibold text-xs"
            >
              Cancelar
            </button>
            <button
              data-testid="btn-submit-group"
              type="submit"
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-2 rounded-xl text-xs uppercase shadow-[0_0_15px_rgba(212,175,55,0.3)]"
            >
              {isEditing ? "Salvar Alterações" : "Criar Grupo"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};
